import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './DropdownMenu.css';

const GAP = 6;

// Menu déroulant : bouton + panneau d'actions. Le panneau est rendu dans <body> en position fixe,
// pour ne pas être coupé par un parent qui masque son débordement (barre d'outils de MyDraft).
// items : [{ key, label, description?, icon?, onSelect, disabled?, danger? }] ; header / footer : contenu libre.
function DropdownMenu({ label, ariaLabel, items, header, footer, align = 'left', buttonClassName = '', disabled = false }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();

  const close = useCallback((focusButton = false) => {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth || 0;
    const left = align === 'right' ? rect.right - menuWidth : rect.left;
    setPosition({
      top: rect.bottom + GAP,
      left: Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8)),
    });
  }, [open, align]);

  useEffect(() => {
    if (!open) return undefined;
    menuRef.current?.querySelector('[role="menuitem"]:not(:disabled)')?.focus();

    const onPointerDown = (event) => {
      if (!menuRef.current?.contains(event.target) && !buttonRef.current?.contains(event.target)) close();
    };
    const onViewportChange = () => close();
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [open, close]);

  const onMenuKeyDown = (event) => {
    const entries = Array.from(menuRef.current.querySelectorAll('[role="menuitem"]:not(:disabled)'));
    const index = entries.indexOf(document.activeElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
    } else if (event.key === 'Tab') {
      close();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      entries[(index + step + entries.length) % entries.length]?.focus();
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      entries[event.key === 'Home' ? 0 : entries.length - 1]?.focus();
    }
  };

  const select = (item) => {
    close(true);
    item.onSelect();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`DropdownMenu-button ${buttonClassName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {label}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            className="DropdownMenu-panel"
            style={position ? { top: position.top, left: position.left } : { visibility: 'hidden', top: 0, left: 0 }}
            onKeyDown={onMenuKeyDown}
          >
            {header}
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className={`DropdownMenu-item${item.danger ? ' DropdownMenu-item--danger' : ''}`}
                disabled={item.disabled}
                onClick={() => select(item)}
              >
                {item.icon && <span className="DropdownMenu-icon">{item.icon}</span>}
                <span className="DropdownMenu-text">
                  <span className="DropdownMenu-label">{item.label}</span>
                  {item.description && <span className="DropdownMenu-description">{item.description}</span>}
                </span>
              </button>
            ))}
            {footer}
          </div>,
          document.body
        )}
    </>
  );
}

export default DropdownMenu;
